import { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, Split } from 'lucide-react';
import type { PedidoDetalle, PedidoItemVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { SpinnerInline } from '@renderer/components/ui/SpinnerInline';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  abierto: boolean;
  pedidoId: number | null;
  onClose: () => void;
  onDividido: () => void;
}

interface GrupoLocal {
  id: string;
  etiqueta: string;
  itemIds: Set<number>;
}

const COLORES_GRUPO = [
  'bg-blue-100 text-blue-800 border-blue-300',
  'bg-emerald-100 text-emerald-800 border-emerald-300',
  'bg-purple-100 text-purple-800 border-purple-300',
  'bg-amber-100 text-amber-800 border-amber-300',
  'bg-pink-100 text-pink-800 border-pink-300',
  'bg-cyan-100 text-cyan-800 border-cyan-300',
];

/**
 * Modal para dividir un pedido en N sub-pedidos por items (Sprint 6 RN-S6-03..05).
 *
 * Flujo:
 *  1. Carga el detalle del pedido (solo items activos).
 *  2. Permite agregar grupos (mín. 2) y asignar cada item a UN grupo.
 *  3. Validación local: cada grupo ≥1 item, sin duplicados (el chip muestra el grupo asignado).
 *  4. Al confirmar invoca pedidos.dividirCuenta y avisa al padre para refrescar.
 */
export function DividirCuentaModal({ abierto, pedidoId, onClose, onDividido }: Props) {
  const [detalle, setDetalle] = useState<PedidoDetalle | null>(null);
  const [grupos, setGrupos] = useState<GrupoLocal[]>([]);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const negocio = useNegocioStore((s) => s.negocio);
  const sesion = useSessionStore((s) => s.sesion);

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  useEffect(() => {
    if (!abierto || !pedidoId) {
      setDetalle(null);
      setGrupos([]);
      setError(null);
      return;
    }
    setCargando(true);
    window.api.pedidos.obtener(pedidoId).then((res) => {
      if (res.ok) {
        setDetalle(res.data);
        // Inicializar con 2 grupos vacíos
        setGrupos([
          { id: crypto.randomUUID(), etiqueta: 'Grupo 1', itemIds: new Set() },
          { id: crypto.randomUUID(), etiqueta: 'Grupo 2', itemIds: new Set() },
        ]);
      }
      setCargando(false);
    });
  }, [abierto, pedidoId]);

  const itemsActivos = useMemo<PedidoItemVista[]>(
    () => detalle?.items.filter((i) => i.estado === 'activo') ?? [],
    [detalle],
  );

  const grupoIdDeItem = (itemId: number): string | null => {
    for (const g of grupos) {
      if (g.itemIds.has(itemId)) return g.id;
    }
    return null;
  };

  const subtotalGrupo = (g: GrupoLocal): number => {
    let s = 0;
    for (const it of itemsActivos) {
      if (g.itemIds.has(it.id)) s += it.subtotal;
    }
    return s;
  };

  const asignarItemAGrupo = (itemId: number, grupoId: string | null) => {
    setGrupos((prev) =>
      prev.map((g) => {
        const next = new Set(g.itemIds);
        next.delete(itemId);
        if (g.id === grupoId) next.add(itemId);
        return { ...g, itemIds: next };
      }),
    );
  };

  const agregarGrupo = () => {
    if (grupos.length >= 6) return;
    setGrupos((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        etiqueta: `Grupo ${prev.length + 1}`,
        itemIds: new Set(),
      },
    ]);
  };

  const eliminarGrupo = (grupoId: string) => {
    if (grupos.length <= 2) return;
    setGrupos((prev) => prev.filter((g) => g.id !== grupoId));
  };

  const renombrarGrupo = (grupoId: string, nuevo: string) => {
    setGrupos((prev) =>
      prev.map((g) => (g.id === grupoId ? { ...g, etiqueta: nuevo.slice(0, 50) } : g)),
    );
  };

  const itemsAsignados = grupos.reduce((acc, g) => acc + g.itemIds.size, 0);
  const itemsSinAsignar = itemsActivos.length - itemsAsignados;
  const algunGrupoVacio = grupos.some((g) => g.itemIds.size === 0);
  const puedeDividir = grupos.length >= 2 && !algunGrupoVacio && !enviando;

  const handleConfirmar = async () => {
    if (!pedidoId || !sesion) return;
    if (algunGrupoVacio) {
      setError('Cada grupo debe tener al menos un item.');
      return;
    }
    setEnviando(true);
    setError(null);
    const res = await window.api.pedidos.dividirCuenta({
      pedidoId,
      grupos: grupos.map((g) => ({
        items: Array.from(g.itemIds),
        etiqueta: g.etiqueta || undefined,
      })),
      usuarioId: sesion.usuarioId,
    });
    setEnviando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    onDividido();
    onClose();
  };

  if (!abierto) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Split className="h-5 w-5 text-primary" />
              Dividir cuenta
            </h2>
            {detalle && (
              <p className="text-xs text-slate-500">
                Pedido #{String(detalle.numeroPedido).padStart(5, '0')} ·
                Total original: {formatPrecio(detalle.total, monedaSimbolo, locale)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar" disabled={enviando}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {cargando ? (
            <SpinnerInline texto="Cargando pedido…" className="py-8" />
          ) : !detalle || itemsActivos.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              No hay items activos para dividir.
            </div>
          ) : (
            <>
              {/* Grupos */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-700">Grupos</h3>
                  <button
                    type="button"
                    onClick={agregarGrupo}
                    className="btn-ghost text-xs flex items-center gap-1"
                    disabled={grupos.length >= 6 || enviando}
                  >
                    <Plus className="h-3 w-3" /> Agregar grupo
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {grupos.map((g, idx) => {
                    const color = COLORES_GRUPO[idx % COLORES_GRUPO.length];
                    const subtotal = subtotalGrupo(g);
                    return (
                      <div
                        key={g.id}
                        className={`rounded-lg border-2 px-3 py-2 ${color} relative`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={g.etiqueta}
                            onChange={(e) => renombrarGrupo(g.id, e.target.value)}
                            className="flex-1 bg-transparent outline-none font-semibold text-sm"
                            maxLength={50}
                            disabled={enviando}
                          />
                          {grupos.length > 2 && (
                            <button
                              onClick={() => eliminarGrupo(g.id)}
                              className="opacity-60 hover:opacity-100"
                              aria-label="Eliminar grupo"
                              disabled={enviando}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs">
                          <span>{g.itemIds.size} item{g.itemIds.size === 1 ? '' : 's'}</span>
                          <span className="font-mono font-bold">
                            {formatPrecio(subtotal, monedaSimbolo, locale)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Items para asignar */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">
                  Items ({itemsSinAsignar} sin asignar)
                </h3>
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {itemsActivos.map((it) => {
                    const grupoActualId = grupoIdDeItem(it.id);
                    return (
                      <li key={it.id} className="px-3 py-2 flex items-center gap-2">
                        <div className="flex-1">
                          <div className="text-sm font-medium">
                            {it.cantidad}× {it.productoNombre ?? it.paqueteNombre}
                          </div>
                          <div className="text-xs text-slate-500 font-mono">
                            {formatPrecio(it.subtotal, monedaSimbolo, locale)}
                          </div>
                        </div>
                        <select
                          value={grupoActualId ?? ''}
                          onChange={(e) =>
                            asignarItemAGrupo(it.id, e.target.value || null)
                          }
                          className="input text-xs py-1 max-w-[160px]"
                          disabled={enviando}
                        >
                          <option value="">— sin asignar —</option>
                          {grupos.map((g, idx) => (
                            <option key={g.id} value={g.id}>
                              {idx + 1}. {g.etiqueta}
                            </option>
                          ))}
                        </select>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Resumen */}
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm">
                <div className="flex justify-between">
                  <span>Items asignados</span>
                  <span className="font-mono">{itemsAsignados} / {itemsActivos.length}</span>
                </div>
                {itemsSinAsignar > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Los items sin asignar permanecen en el pedido original.
                  </p>
                )}
                {algunGrupoVacio && (
                  <p className="text-xs text-amber-700 mt-1">
                    Algún grupo está vacío. Cada grupo debe tener al menos un item.
                  </p>
                )}
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700" role="alert">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={handleConfirmar}
            className="btn-primary flex-1"
            disabled={!puedeDividir}
          >
            {enviando ? 'Dividiendo...' : `Dividir en ${grupos.length} sub-pedidos`}
          </button>
        </footer>
      </div>
    </div>
  );
}
